import { Image } from 'components/Image';
import { getRecipientPhotoUrl } from 'helpers/imageUrls';
import { FC } from 'react';
import { ContributionSummaryView } from 'store/contributionSummary/types';

export const MAX_CIRCLES = 3;

// Recipients include organisations, so this takes the outer words rather than
// assuming a first and last name. Generational suffixes ("III", "Jr.") are
// dropped first so they don't get treated as the last name.
const NAME_SUFFIXES = new Set(['jr', 'sr', 'ii', 'iii', 'iv', 'v']);

const getInitials = (name: string) => {
  const words = name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .filter(word => !NAME_SUFFIXES.has(word.replace(/\.$/, '').toLowerCase()));
  if (!words.length) return '';
  const last = words.length > 1 ? words[words.length - 1][0] : '';
  return `${words[0][0]}${last}`.toUpperCase();
};

const Circle: FC<{ candidate: ContributionSummaryView }> = ({ candidate }) => {
  const initials = (
    <span className="cycle-candidate-circle">{getInitials(candidate.recipient_name)}</span>
  );

  if (!candidate.photo_id) return initials;

  return (
    <Image
      className="cycle-candidate-circle"
      alt={candidate.recipient_name}
      src={getRecipientPhotoUrl(candidate.photo_id)}
      fallbackPlaceholder={initials}
    />
  );
};

export const CandidateCircles: FC<{ candidates: ContributionSummaryView[] }> = ({ candidates }) => {
  if (!candidates.length) return null;

  const overflow = candidates.length - MAX_CIRCLES;

  return (
    // Decorative: these render inside the metric tile's button, and recipient
    // names would otherwise be folded into its accessible name.
    <div className="cycle-candidate-circles" aria-hidden="true">
      {candidates.slice(0, MAX_CIRCLES).map(candidate => (
        <Circle key={candidate.recipient_id} candidate={candidate} />
      ))}
      {overflow > 0 && <span className="cycle-candidate-circle">{`+${overflow}`}</span>}
    </div>
  );
};
